import { createContext, useContext } from 'react';
import { type MembershipRole } from '@aether/client-graphql/generated/graphql';

import { type CurrentOrganization } from '@/utils/auth';

interface OrganizationContextData {
  organization: NonNullable<CurrentOrganization>;
  currentUserRole?: MembershipRole | null;
  removeMember: (clientId: string) => Promise<unknown>;
  addMember: (data: { clientId: string; role: MembershipRole }) => Promise<unknown>;
  inviteMember: (data: { displayName: string; email: string; role: MembershipRole }) => Promise<unknown>;
  updateMemberRole: (data: { clientId: string; role: MembershipRole }) => Promise<unknown>;
  createServiceAccount: (data: { name: string; role: MembershipRole }) => Promise<unknown>;
  updateServiceAccount: (data: { id: string; name: string }) => Promise<unknown>;
  removeServiceAccount: (id: string) => Promise<unknown>;
  createApiKey: (data: { clientId: string; name: string }) => Promise<unknown>;
  updateApiKey: (data: { apiKeyId: string; active: boolean; clientId: string }) => Promise<unknown>;
  removeApiKey: (data: { apiKeyId: string; clientId: string }) => Promise<unknown>;
  createTeam: (data: { name: string; description: string }) => Promise<unknown>;
  updateTeam: (data: { id: string; name: string; description: string }) => Promise<unknown>;
  removeTeam: (id: string) => Promise<unknown>;
  addMemberToTeam: (data: { teamId: string; clientId: string }) => Promise<unknown>;
  removeMemberFromTeam: (data: { teamId: string; clientId: string }) => Promise<unknown>;
}

const OrganizationContext = createContext<OrganizationContextData | null>(null);

export const OrganizationProvider = (props: { children: React.ReactNode; data: OrganizationContextData }) => {
  const { children, data } = props;
  return <OrganizationContext.Provider value={data || null}>{children}</OrganizationContext.Provider>;
};

export const useOrganizationAdmin = () => {
  const context = useContext(OrganizationContext);
  if (!context) throw new Error('useOrganization must be used within an OrganizationProvider');
  return context;
};
